import React, { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

import EyeIcon from "../../assets/EyeIcon";
import ClosedEyeIcon from "../../assets/ClosedEyeIcon";
import { Validation } from "../../types/form";
import { FieldErrorMessage } from "../ui/Text";
import { validateTrim } from "@/utils/helper/string";

interface PasswordInputValidation extends Validation {
  excludeDomains?: string[];
  equalityCheckField: string;
}
interface PasswordInputProps {
  label: string;
  field: string;
  placeholder: string;
  disabled?: boolean;
  validation?: PasswordInputValidation;
}

const PasswordInput: React.FC<PasswordInputProps> = ({ label, field, disabled = false, validation, placeholder }) => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const errorMessage: any = errors[field]?.message;

  const getRegisterOptions = () => {
    const registerOptions: any = {};

    if (validation?.required) {
      registerOptions.required = "This field is required.";
    }
    if (validation?.minLength) {
      registerOptions.minLength = {
        value: validation.minLength,
        message: `The minimum character allowed is ${validation.minLength}.`,
      };
    }
    if (validation?.maxLength) {
      registerOptions.maxLength = {
        value: validation.maxLength,
        message: `The maximum character allowed is ${validation.maxLength}.`,
      };
    }
    if (!validation?.validTrim) {
      registerOptions.validate = {
        ...registerOptions.validate,
        noSpacesAllowed: validateTrim,
      };
    }
    if (validation?.pattern) {
      registerOptions.validate = {
        ...registerOptions.validate,
        pattern: (value: string) => {
          const patternValue = validation?.pattern?.value;
          if (!patternValue) throw Error("Invalid Pattern");

          let regex: RegExp;
          try {
            regex = new RegExp(patternValue);
          } catch (error) {
            console.error("Invalid RegExp:", error);
            return "Invalid pattern configuration";
          }
          return regex.test(value) || validation.pattern?.message || "Invalid format";
        },
      };
    }
    if (validation?.equalityCheckField) {
      registerOptions.validate = {
        ...registerOptions.validate,
        equalityCheck: (value: string, formValues: Record<string, any>) => {
          const compareValue = formValues[validation.equalityCheckField];
          return value === compareValue || `Password does not match, please try again!`;
        },
      };
    }

    return registerOptions;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent space key
    if (e.key === " " || e.code === "Space") {
      e.preventDefault();
      return false;
    }
  }, []);

  return (
    <div>
      <div className=" relative">
        <input
          type={showPassword ? "text" : "password"}
          id={label + "_password"}
          placeholder={placeholder}
          className={`w-full pr-10 px-3 py-7 text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500 ${
            errors[field]?.message ? "border-text-error" : null
          } ${disabled ? "cursor-not-allowed" : "cursor-text"}`}
          {...register(field, {
            ...getRegisterOptions(),
          })}
          onKeyDown={(e) => handleKeyDown(e)}
          disabled={disabled}
          autoComplete="off"
        />
        <button type="button" onClick={togglePasswordVisibility} className="absolute inset-y-0 right-0 pr-3 flex items-center">
          {showPassword ? <EyeIcon className="h-5 w-5 text-gray-400" /> : <ClosedEyeIcon className="h-5 w-5 text-gray-400" />}
        </button>
      </div>
      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </div>
  );
};

export default PasswordInput;
